import { formatSentAt } from '@/lib/order-storage'
import type { ChatSendOrigin } from '@/lib/chat-motion'
import type { MessageDispatch } from '@/lib/types'

export function MessageHeader({
  title,
  subtitle,
  messageState,
  onToggleManualMark,
  markingManual,
  actions,
}: {
  title: string
  subtitle?: string
  messageState?: MessageDispatch
  onToggleManualMark?: () => void
  markingManual?: boolean
  actions?: React.ReactNode
}) {
  return (
    <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
      <div>
        <h3 className="text-[18px] font-semibold text-ink tracking-tight">{title}</h3>
        {subtitle && <div className="text-[12.5px] text-ink-muted mt-0.5">{subtitle}</div>}
      </div>
      <div className="flex items-center gap-2.5 flex-wrap">
        {messageState && messageState.origin !== 'operator_manual_mark' && (
          <span className="text-[11px] tracking-[0.06em] uppercase text-st-delivered font-semibold flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-st-delivered" />
            Отправлено {formatSentAt(messageState.dispatchedAt)}
          </span>
        )}
        {messageState?.origin === 'operator_manual_mark' && (
          <span className="text-[11px] tracking-[0.06em] uppercase text-gold-deep font-semibold flex items-center gap-1.5">
            <span className="size-1.5 rounded-full bg-gold-deep" />
            Отмечено вручную {formatSentAt(messageState.dispatchedAt)}
          </span>
        )}
        {messageState?.origin !== 'automatic' && messageState?.origin !== 'operator_send' && onToggleManualMark && (
          <button type="button" onClick={onToggleManualMark} disabled={markingManual} className="btn btn-sm btn-ghost text-ink-muted">
            {markingManual ? 'Сохраняем…' : messageState ? 'Снять отметку' : 'Отметить отправленным'}
          </button>
        )}
        {actions}
      </div>
    </div>
  )
}

// The text below a sent header is re-rendered from today's data, not the text the buyer received.
export function SentTextNote() {
  return (
    <div className="mb-2 text-[12px] text-ink-muted">
      Текст для повторной отправки. Отправленное сообщение — в чате Маркета.
    </div>
  )
}

export function MessageBlock({
  title,
  subtitle,
  messageState,
  onToggleManualMark,
  markingManual,
  text,
  missing,
  copied,
  onCopy,
  onSend,
  sending,
  sendingDisabled,
  emptyText,
}: {
  title: string
  subtitle?: string
  messageState?: MessageDispatch
  onToggleManualMark?: () => void
  markingManual?: boolean
  text: string | null
  missing: string[]
  copied: boolean
  onCopy: () => void
  onSend?: (origin: ChatSendOrigin) => void
  sending: boolean
  sendingDisabled: boolean
  emptyText?: string
}) {
  return (
    <div className="mb-8">
      <MessageHeader
        title={title}
        subtitle={subtitle}
        messageState={messageState}
        onToggleManualMark={onToggleManualMark}
        markingManual={markingManual}
        actions={
          text && (
            <>
              <button onClick={onCopy} className={`btn btn-sm ${copied ? 'btn-gold' : 'btn-ghost'}`}>
                {copied ? 'Скопировано' : 'Копировать'}
              </button>
              {onSend && (
                <button
                  onClick={(event) => onSend({ x: event.clientX, y: event.clientY })}
                  disabled={sendingDisabled}
                  className={`btn btn-sm ${sending ? 'btn-ghost cursor-wait' : 'btn-primary'}`}
                >
                  {sending ? 'Отправка…' : messageState ? 'Отправить снова' : 'Отправить'}
                </button>
              )}
            </>
          )
        }
      />
      {text ? (
        <>
          {messageState && <SentTextNote />}
          <div className="surface-card rounded-md p-4 sm:p-6 text-[14px] leading-[1.6] text-ink-2 whitespace-pre-wrap break-words">
            {text}
          </div>
        </>
      ) : emptyText ? (
        <div className="surface-card rounded-md p-4 text-[13px] leading-relaxed text-ink-muted">{emptyText}</div>
      ) : missing.length > 0 ? (
        <div className="px-4 py-3 rounded-md border border-rule-strong bg-bone/60 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] tracking-[0.08em] uppercase text-ink-muted font-semibold">
            Заполните
          </span>
          {missing.map((f) => (
            <span key={f} className="px-2 py-0.5 rounded-sm bg-paper border border-rule-strong text-ink-2 text-[12px]">
              {f}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  )
}
